<template>
  <div
    v-if="$globals.showSiteLoader"
    id="app"
    class="full-page-loader"
  >
    <Loader />
  </div>

  <div
    v-else
    id="app"
  >
    <component :is="layout">
      <b-alert
        ref="successAlert"
        :show="successDismissCountDown"
        variant="success"
        dismissible
        fade
        @dismissed="dismissAlert()"
      >
        <span v-html="$globals.globalSuccessAlertText" />
      </b-alert>

      <b-alert
        ref="errorAlert"
        :show="errorDismissCountDown"
        variant="danger"
        dismissible
        fade
        @dismissed="dismissAlert()"
      >
        <span v-html="$globals.globalErrorAlertText" />
      </b-alert>
      <!-- keyed so a confirmed assistant action on the record this page
           shows can remount it with fresh data; the panel sits outside -->
      <router-view :key="assistantPanelStore.pageRefreshTick" />
      <session-modal ref="sessionModal" />

      <accept-terms-modal
        ref="termsModal"
        :on-accept="termsAccepted"
        :on-reject="termsRejected"
      />

      <setup-mfa-token-modal
        v-if="$globals.user()"
        ref="setupMfaTokenModal"
        body="In the future it will be required that owner accounts have two factor authentication enabled. Please set this up now."
      />

      <CoolLightBox
        v-if="$globals.lightboxImages"
        :items="$globals.lightboxImages.map(i => i.url)"
        :index="$globals.lightboxIndex"
        @close="$globals.lightboxIndex = null"
      />

      <task-timer />
      <agent-panel v-if="assistantAvailable" />
    </component>
  </div>
</template>

<script>
import { mapStores } from 'pinia';
import CachedDataHelper from '@/cache/CachedDataHelper';
import 'vue-cool-lightbox/dist/vue-cool-lightbox.min.css';

import UserClient from '@/api/UserClient';
import { useAssistantPanelStore } from '@/store/modules/assistantPanel';
import NotificationClient from '@/api/NotificationClient';
import VendorHubClient from '@/api/VendorHubClient';
import CompanyClient from '@/api/CompanyClient';
import RelatedCompaniesClient from '@/api/RelatedCompaniesClient';
import AdminClient from '@/api/AdminClient';
import AffiliateClient from '@/api/AffiliateClient';
import MarketplaceClient from '@/api/MarketplaceClient';
import InfoClient from '@/api/InfoClient';
import WeatherClient from '@/api/WeatherClient';
import SuggestionClient from '@/api/SuggestionClient';
import WorkOrderRequestClient from '@/api/WorkOrderRequestClient';
import PMScheduleClient from '@/api/PMScheduleClient';
import ReservationClient from '@/api/ReservationClient';
import PreventClient from '@/api/PreventClient';
import McpOAuthClient from '@/api/McpOAuthClient';
import ChatClient from '@/api/ChatClient';
import FleetClient from '@/api/FleetClient';
import { defineAsyncComponent } from 'vue';
import TemplateLibraryClient from './api/TemplateLibraryClient.js';

const ALERT_TIMEOUT = 5;

export default {
  name: 'App',
  components: {
    AcceptTermsModal: defineAsyncComponent(() => import('@/components/modals/AcceptTermsModal.vue')),
    CoolLightBox: defineAsyncComponent(() => import('vue-cool-lightbox')),
    Loader: defineAsyncComponent(() => import('@/components/Loader.vue')),
    SessionModal: defineAsyncComponent(() => import('@/components/modals/SessionModal.vue')),
    SetupMfaTokenModal: defineAsyncComponent(() => import('@/components/modals/SetupMfaTokenModal.vue')),
    TaskTimer: () => import('@/components/TaskTimer.vue'),
    AgentPanel: () => import('@/components/agent/AgentPanel.vue'),
  },
  data() {
    return {
      successDismissCountDown: 0,
      errorDismissCountDown: 0,

      initialStyleTagValue: '',

      appcuesUrl: 'https://fast.appcues.com/106194.js',
      fontAwesomeKitUrl: 'https://kit.fontawesome.com/a8f97f61dd.js',
      zendeskUrl: 'https://static.zdassets.com/ekr/snippet.js?key=94eb0997-a41b-4d0d-b48b-fe47253c2529',
    };
  },
  computed: {
    layout() {
      return (this.$route.meta.layout || 'default-layout');
    },
    ...mapStores(useAssistantPanelStore),
    // the floating assistant panel lives at the layout level so it can
    // follow the user across pages; flag + company capability gate it
    assistantAvailable() {
      return !!this.$globals.user()
        && this.$globals.isFlagEnabled('helix_ai')
        && this.$globals.isCapabilityAvailable('helix_ai');
    },
    isViewingAffiliatePortal() {
      return this.$route.path.startsWith('/affiliate');
    },
    isViewingOwnerPortal() {
      return this.$route.path.startsWith('/owner') || this.$route.path.startsWith('/admin');
    },
  },
  watch: {
    $route() {
      // ensure user has previously accepted TOS
      this.checkTerms();
      this.checkMfa();
      this.toggleZendeskElement();
    },
    // watch the global alert triggers to show the alert here
    '$globals.globalSuccessAlertText': function (val) {
      if (val) {
        this.successDismissCountDown = this.$globals.globalAlertDelay || ALERT_TIMEOUT;

        // hack to add a "open" class when alert appears to have it animate down nicely
        setTimeout(() => {
          if (this.$refs.successAlert?.$el?.classList) {
            this.$refs.successAlert.$el.classList.add('open');
          }
        }, 300);
      } else {
        this.successDismissCountDown = 0;
      }
    },
    '$globals.globalErrorAlertText': function (val) {
      if (val) {
        this.errorDismissCountDown = this.$globals.globalAlertDelay || ALERT_TIMEOUT;

        // hack to add a "open" class when alert appears to have it animate down nicely
        setTimeout(() => {
          if (this.$refs.errorAlert?.$el?.classList) {
            this.$refs.errorAlert.$el.classList.add('open');
          }
        }, 300);
      } else {
        this.errorDismissCountDown = 0;
      }
    },

    // when we close the alert here, update the global
    successDismissCountDown(val) {
      if (val <= 0) {
        this.$globals.clearGlobalAlert = false;
      }
    },
    errorDismissCountDown(val) {
      if (val <= 0) {
        this.$globals.clearGlobalAlert = true;
      }
    },
  },
  created() {
    window.addEventListener('focus', this.windowFocus);
  },
  destroyed() {
    window.removeEventListener('focus', this.windowFocus);
  },
  mounted() {
    this.initializeHttpClients();

    this.$globals.cachedData = new CachedDataHelper();
    this.$globals.sessionModal = () => this.$refs.sessionModal;

    this.$globals.adminClient.getGlobalSettings().then((res) => {
      this.$globals.globalSettings = {
        show_self_signup: (res.items || []).filter(r => r.boolean_value && r.identifier === 'self_registration').length > 0,
        require_confirm_email: (res.items || []).filter(r => r.boolean_value && r.identifier === 'require_email_verification').length > 0,
      };
    }).catch(() => {
      this.$globals.globalSettings = {
        show_self_signup: false,
        require_confirm_email: true,
      };
    });

    this.addMobileHooks();

    // TODO: Remove this once we fully move to HttpOnly cookies
    if (this.$globals.accessToken || this.$globals.userId) {
      // we may have to prevent the tracking until they verify equipment or accept terms
      // so reset it until we can check that
      this.$globals.removeTracking();
      this.authorizeUser();
    } else {
      this.doneLoading();
      this.failedToAuth();
    }

    this.$nextTick(() => {
      // Do this immediately after load to avoid missing icons
      this.$loadScript(this.fontAwesomeKitUrl);
      setTimeout(() => {
        // Do this after a delay to avoid blocking the page load
        this.$loadScript(this.appcuesUrl);
        // Couldn't use vue-plugin-load-script directly since ZenDesk inspects the DOM for the src and id of the element...
        this.toggleZendeskElement();
      }, 2500);
    });

    // close dropdowns when the user scrolls the window
    this.$root.$on('bv::dropdown::show', (bvEvent) => {
      window.addEventListener('scroll', () => {
        bvEvent.vueTarget.hide();
      }, { once: true });
    });
  },
  methods: {
    initializeHttpClients() {
      this.$globals.userClient = new UserClient(this.$http);
      this.$globals.notificationClient = new NotificationClient(this.$http);
      this.$globals.companyClient = new CompanyClient(this.$http);
      this.$globals.relatedCompaniesClient = new RelatedCompaniesClient(this.$http);
      this.$globals.adminClient = new AdminClient(this.$http);
      this.$globals.affiliateClient = new AffiliateClient(this.$http);
      this.$globals.marketplaceClient = new MarketplaceClient(this.$http);
      this.$globals.infoClient = new InfoClient(this.$http);
      this.$globals.weatherClient = new WeatherClient(this.$http);
      this.$globals.suggestionClient = new SuggestionClient(this.$http);
      this.$globals.vendorHubClient = new VendorHubClient(this.$http);
      this.$globals.workOrderRequestClient = new WorkOrderRequestClient(this.$http);
      this.$globals.pmScheduleClient = new PMScheduleClient(this.$http);
      this.$globals.reservationClient = new ReservationClient(this.$http);
      this.$globals.preventClient = new PreventClient(this.$http);
      this.$globals.templateLibraryClient = new TemplateLibraryClient(this.$http);
      this.$globals.mcpOAuthClient = new McpOAuthClient(this.$http);
      this.$globals.chatClient = new ChatClient(this.$http);
      this.$globals.fleetClient = new FleetClient(this.$http);
    },
    addZendeskElement() {
      if (this.$route.name === 'print-task') return;
      if (this.isViewingAffiliatePortal) return;

      const el = document.createElement('script');
      el.type = 'text/javascript';
      el.async = true;
      el.src = this.zendeskUrl;
      el.id = 'ze-snippet';
      document.head.appendChild(el);
    },
    removeZendeskElement() {
      const zendeskScript = document.getElementById('ze-snippet');
      if (zendeskScript?.parentNode) {
        zendeskScript.parentNode.removeChild(zendeskScript);
      }

      const launcher = document.getElementById('launcher');
      if (launcher?.parentNode) {
        launcher.parentNode.removeChild(launcher);
      }

      delete window.zE;
      delete window.zEmbed;
      delete window.zEACLoaded;
      delete window.zEQueue;
      delete window.zESettings;

      const messengerContainer = document.getElementById('web-messenger-container');
      if (messengerContainer?.parentNode) {
        messengerContainer.parentNode.removeChild(messengerContainer);
      }
    },
    toggleZendeskElement() {
      if (this.isViewingAffiliatePortal) {
        this.removeZendeskElement();
        return;
      }

      if (!document.getElementById('ze-snippet')) {
        this.addZendeskElement();
      }
    },
    checkTerms() {
      this.$nextTick(() => {
        const viewingContact = this.$route.name === 'contact'; // let user contact us without verification modals
        const mustAcceptTOS = !viewingContact && this.$globals.currentUser?.require_terms_acceptance;

        if (mustAcceptTOS) {
          this.$refs.termsModal?.show();
        }
      });
    },
    async termsAccepted() {
      this.$refs.termsModal.hide();
    },
    termsRejected() {
      return this.$router.push({ name: 'logout' });
    },
    checkMfa() {
      this.$nextTick(() => {
        const mustEnableMfa = this.$globals.isFlagEnabled('login_check_mfa') && this.$globals.user() && this.$globals.user().isOwner && !this.$globals.user().mfa_enabled;

        if (mustEnableMfa) {
          this.$refs.setupMfaTokenModal.show();
        }
      });
    },
    dismissAlert() {
      this.successDismissCountDown = 0;
      this.$globals.globalSuccessAlertText = '';
      this.errorDismissCountDown = 0;
      this.$globals.globalErrorAlertText = '';
    },
    windowFocus() {
      // also trigger a window resize event
      setTimeout(() => {
        window.dispatchEvent(new Event('resize'));
      }, 300);
    },
    async failedToAuth() {
      // need to do this after we load the user/shadow
      this.$globals.checkUrlAffiliate();

      this.$globals.clearUser();
      await this.$globals.refreshFeatureFlags();

      // Seems like we can't use this.$route here since it might not have fully loaded the page yet.
      const routeAllowsAnonymous = this.$router.resolve(window.location.pathname).route?.meta?.allowAnonymous == true;

      // check if current route allows anonymous view
      if (!routeAllowsAnonymous) {
        return this.$router.push({ name: 'login', params: { destination: window.location.href } });
      }
    },
    doneLoading() {
      // Keep the loader up while leaving for the policyholder portal so no v1 UI flashes.
      if (this.$globals.redirectingToPolicyholderPortal) return;

      setTimeout(() => {
        this.$globals.AppLoader = false;
        this.$globals.showSiteLoader = false;
        this.checkTerms();
        this.checkMfa();
      }, 300);
    },
    authorizeUser() {
      return this.$globals.userClient.getRemoteUser().then(async (user) => {
        if (user) {
          this.$globals.setUser(user);
          this.$globals.activateTracking();
          this.$globals.setRefreshToken(true);
          await this.$globals.loadShadows();

          const queryParams = Object.fromEntries(new URLSearchParams(document.location.search));

          let companyId = queryParams.company_id || this.$globals.JUST_JOINED_COMPANY || this.$globals.companyId;

          if (companyId && !user.companies.find(x => x.id === companyId)) {
            // check for an invalid companyId based on the users current list of companies
            companyId = null;
          }

          if (!companyId && user.currentCompany) {
            companyId = user.currentCompany.id;
          }

          this.$globals.JUST_JOINED_COMPANY = '';
          this.$globals.saveCookie();

          // look for shadowUser and shadowCompany
          const shadowUser = this.$globals.shadowUser;
          const shadowCompany = this.$globals.shadowCompany;

          const lastUsedDashboard = this.$globals.selectedDashboard;

          if (shadowUser) {
            await this.$globals.loginAsUser(shadowUser);
          } else if (shadowCompany) {
            await this.$globals.setCompanyId(shadowCompany.id, shadowCompany);
          } else if (companyId) {
            await this.$globals.setCompanyId(companyId);
          } else {
            // make sure users that are only affiliate managers get the feature flags as well
            await this.$globals.refreshFeatureFlags();
          }

          if (await this.$globals.redirectToPolicyholderPortal()) return;

          if (!shadowUser && !shadowCompany && lastUsedDashboard && lastUsedDashboard !== 'eo') {
            this.$globals.setDashboard(lastUsedDashboard);
          }

          this.$globals.getUnreadCounts();
          this.$globals.testRoutePermission();

          // need to do this after we load the user/shadow
          this.$globals.checkUrlAffiliate();
        } else {
          this.failedToAuth();
        }
      }).catch((_error) => {
        this.failedToAuth();
      }).finally(this.doneLoading);
    },
    addMobileHooks() {
      if (!this.$globals.isInFlutterApp()) {
        return;
      }

      if (!window.HelixIntelMobileSetPushToken) {
        window.HelixIntelMobileSetPushToken = (token, device_id, platform) => {
          try {
            this.$globals.setPushToken(token, device_id, platform);
          } catch (error) {
            console.error('Error setting push token', error);
          }
        };
      }

      if (!window.HelixIntelMobileSetKeyboardState) {
        window.HelixIntelMobileSetKeyboardState = (open, height) => {
          try {
            this.$globals.setMobileKeyboardState(open, height);
          } catch (error) {
            console.error('Error setting mobile keyboard state', error);
          }
        };
      }
    },
  },
};

</script>

<style lang="scss">
@import "@/assets/styles/styles";

.alert {
  z-index: 9999;
}
</style>
